import { Combobox, useCombobox } from "@mantine/core";
import { type Dispatch, type FC, type SetStateAction, useCallback, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { FormattedMessage, useIntl } from "react-intl";
import { DropdownTrigger } from "./DropdownTrigger.js";
import { IconCalendar } from "@tabler/icons-react";
import { format } from "date-fns";
import { dateFnsLocales } from "../../../../constants/dateFnsLocales.js";
import { DatePickerSection } from "./DatePickerSection.js";
import type { DateRange } from "../constant/dateRange.js";
import { DATE_RANGE_PRESET, type PresetDateRangeKey } from "../constant/dateRange.js";

export interface DateRangeSelectorProps {
  dateRange: DateRange;
  setDateRange: Dispatch<SetStateAction<DateRange>>;
}

export const DateRangeSelector: FC<DateRangeSelectorProps> = ({
  dateRange,
  setDateRange,
}) => {
  const intl = useIntl();

  const [opened, {
    close: closeCombobox,
    toggle: toggleCombobox,
  }] = useDisclosure(false);
  const combobox = useCombobox({ opened });
  const [openedDatePicker, {
    open: openDatePicker,
    close: closeDatePicker,
  }] = useDisclosure(false);

  const handleCloseDatePicker = useCallback(() => {
    closeDatePicker();
    closeCombobox();
  }, [closeDatePicker]);

  const [keyOfPresetRange, setKeyOfPresetRange] = useState<PresetDateRangeKey | null>("thisPeriod");

  return (
    <Combobox
      onDismiss={handleCloseDatePicker}
      store={combobox}
      position="bottom-start"
      width={openedDatePicker ? "auto" : "target"}
      onOptionSubmit={(value) => {
        if (value === "custom") {
          setKeyOfPresetRange(null);
          openDatePicker();
        }
        else {
          const key = value as PresetDateRangeKey;
          setKeyOfPresetRange(key);
          setDateRange(DATE_RANGE_PRESET[key].dateRange);
          closeCombobox();
        }
      }}
    >
      <Combobox.Target>
        <DropdownTrigger
          opened={opened}
          toggle={() => {
            closeDatePicker();
            toggleCombobox();
          }}
          leftSection={<IconCalendar />}
          text={keyOfPresetRange !== null ?
              intl.formatMessage(DATE_RANGE_PRESET[keyOfPresetRange].message) :
              intl.formatMessage({
                defaultMessage: "{from} - {to}",
                id: "billing.period",
              }, {
                from: format(dateRange[0], "P", {
                  locale: dateFnsLocales[intl.locale],
                }),
                to: format(dateRange[1], "P", {
                  locale: dateFnsLocales[intl.locale],
                }),
              })}
        />
      </Combobox.Target>
      {!openedDatePicker && (
        <Combobox.Dropdown>
          <Combobox.Option value="custom">
            <FormattedMessage defaultMessage="Custom period" id="options.customPeriod" />
          </Combobox.Option>
          {Object.entries(DATE_RANGE_PRESET).map(([key, range]) => (
            <Combobox.Option value={key} key={key}>
              {intl.formatMessage(range.message)}
            </Combobox.Option>
          ))}
        </Combobox.Dropdown>
      )}

      {openedDatePicker && (
        <Combobox.Dropdown>
          <DatePickerSection
            dateRange={dateRange}
            setDateRange={setDateRange}
            closeDatePicker={handleCloseDatePicker}
          />
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};
